import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  PiggyBank,
  Receipt,
  BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";

const Budget = () => {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [formData, setFormData] = useState({
    category: "",
    amount: "",
    entry_type: "income",
    description: "",
    date: new Date().toISOString().split('T')[0]
  });

  const incomeCategories = [
    "Government Grant",
    "State Funding",
    "Foundation Grant",
    "Corporate Donation",
    "Individual Donation",
    "Other Income"
  ];

  const expenseCategories = [
    "Vouchers Issued",
    "Administrative",
    "Marketing",
    "Technology",
    "Evaluation",
    "Personnel",
    "Operations",
    "Other Expense"
  ];

  useEffect(() => {
    fetchBudgetData();
  }, [activeTab]);

  const fetchBudgetData = async () => {
    try {
      const params = new URLSearchParams();
      if (activeTab !== "all") params.append("entry_type", activeTab);
      
      const response = await axios.get(`${API}/budget?${params}`);
      setEntries(response.data.items || []);
      setSummary(response.data.summary || { income: 0, expense: 0, balance: 0 });
    } catch (error) {
      toast.error("Failed to fetch budget data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/budget`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success("Budget entry added successfully");
      setShowDialog(false);
      setFormData({
        category: "",
        amount: "",
        entry_type: "income",
        description: "",
        date: new Date().toISOString().split('T')[0]
      });
      fetchBudgetData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add entry");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      await axios.delete(`${API}/budget/${id}`);
      toast.success("Entry deleted");
      fetchBudgetData();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate budget utilization
  const budgetUtilization = summary.income > 0 
    ? (summary.expense / summary.income) * 100 
    : 0;

  return (
    <div className="space-y-6" data-testid="budget-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Budget & Financials
          </h1>
          <p className="text-slate-500 mt-1">Track program funding and expenditures</p>
        </div>
        <Button 
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
          onClick={() => setShowDialog(true)}
          data-testid="add-entry-btn"
        >
          <Plus className="w-4 h-4" />
          Add Entry
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="dashboard-widget stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs text-green-600 font-medium">Income</span>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-slate-900">TBD</div>
              <div className="text-sm text-slate-500 mt-1">Total Income</div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-widget stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <span className="text-xs text-red-600 font-medium">Expenses</span>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-slate-900">TBD</div>
              <div className="text-sm text-slate-500 mt-1">Total Expenses</div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-widget stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <PiggyBank className="w-6 h-6 text-amber-600" />
              </div>
              <span className={`text-xs font-medium ${summary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {summary.balance >= 0 ? 'Surplus' : 'Deficit'}
              </span>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-slate-900">
                TBD
              </div>
              <div className="text-sm text-slate-500 mt-1">Current Balance</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget Utilization */}
      <Card className="dashboard-widget">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Budget Utilization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Funds Utilized</span>
              <span className="font-medium">{budgetUtilization.toFixed(1)}%</span>
            </div>
            <Progress 
              value={Math.min(budgetUtilization, 100)} 
              className="h-4"
            />
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-amber-500 rounded"></div>
                <span className="text-slate-600">Spent: TBD</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-slate-200 rounded"></div>
                <span className="text-slate-600">Remaining: TBD</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Transaction History</CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                <TabsTrigger value="income" data-testid="tab-income">Income</TabsTrigger>
                <TabsTrigger value="expense" data-testid="tab-expense">Expenses</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="animate-pulse">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No entries found
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                    <TableCell className="text-slate-600">
                      {formatDate(entry.date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          entry.entry_type === 'income' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {entry.entry_type === 'income' 
                            ? <TrendingUp className="w-4 h-4 text-green-600" />
                            : <Receipt className="w-4 h-4 text-red-600" />
                          }
                        </div>
                        <span className="font-medium">{entry.category}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-xs truncate">
                      {entry.description}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        entry.entry_type === 'income' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {entry.entry_type}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right font-medium ${
                      entry.entry_type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {entry.entry_type === 'income' ? '+' : '-'}{formatCurrency(entry.amount)}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(entry.id)}
                        className="text-slate-400 hover:text-red-600"
                        data-testid={`delete-${entry.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Budget Entry</DialogTitle>
            <DialogDescription>
              Record income or expense for the program
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="entry_type">Entry Type *</Label>
              <Select 
                value={formData.entry_type} 
                onValueChange={(v) => setFormData({...formData, entry_type: v, category: ""})}
              >
                <SelectTrigger data-testid="select-entry-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="category">Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({...formData, category: v})}
              >
                <SelectTrigger data-testid="select-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {(formData.entry_type === 'income' ? incomeCategories : expenseCategories).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="amount">Amount ($) *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  className="pl-9"
                  required
                  data-testid="input-amount"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                data-testid="input-date"
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the transaction"
                required
                data-testid="input-description"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className={formData.entry_type === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
                data-testid="submit-btn"
              >
                Add {formData.entry_type === 'income' ? 'Income' : 'Expense'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Budget;
